/**
 * @file functions/convertToOrderState.ts
 * 
 * Utility function to convert a Sylius order response into an OrderState object.
 * 
 * @param order The Sylius order response.
 * @param isLoggedIn Optional flag indicating the user's authentication status.
 */

// Function Imports
import { calculateTotalQuantity } from '@/lib/functions/calculateTotalQuantity';

// Type Imports
import type { OrderState } from '@/lib/types/orderState';
import type { OrderPayload } from '@/lib/types/orderPayload';
import type { SyliusOrderResponse } from '@/lib/types/syliusOrderResponse';

// Helper Function Definitions
function isOrderPayload(order: SyliusOrderResponse | OrderPayload): order is OrderPayload {
  return 'token' in order && !('tokenValue' in order);
}

export function convertToOrderState(
  order: SyliusOrderResponse | OrderPayload,
  tokenType?: 'customer' | 'guest',
): OrderState {
  const itemCount = order?.totalItemQuantityInCart ?? calculateTotalQuantity(order.items);
  const token = isOrderPayload(order)
    ? order.token
    : order.tokenValue;

  return {
    itemCount,
    token,
    tokenType: tokenType || 'guest',
  };
}
